import { Component, Input, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { ProfileMin } from '../../../../profiles/shared/profile-min.model';
import { UtilService, DialogService } from '../../../../shared/index';
import { EventRead } from '../../../shared/event-read.model';
import { EventItemSummary } from '../../../shared/items/event-item-summary.model';
import { IValidationComponent } from '../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { EventCustomRegField } from '../../../shared/custom-reg-fields/event-custom-reg-field.model';
import { EventFieldVisibleRequiredEnum } from '../../../shared/event-field-visible-required.enum';
import { EventV4RegistrationWizardAttendeeComponent } from './event-v4-attendee/event-v4-registration-wizard-attendee.component';
import { ConfigSettingService } from '../../../../config/shared/config-setting/config-setting.service';
import { EventV4ItemService } from 'src/app/events-v4/shared/items/event-v4-item.service';
import { EventV4CustomRegFieldService } from 'src/app/events-v4/shared/custom-reg-fields/event-v4-custom-reg-field.service';
import { EventV4RegistrationService } from 'src/app/events-v4/shared/registration/event-v4-registration.service';
import { RegistrationUpsert } from 'src/app/events-v4/shared/registration/crud/registration-upsert.model';
import { RegistrationAttendee } from 'src/app/events-v4/shared/registration/registration-attendee.model';
import { RegistrationAttendeeItem } from 'src/app/events-v4/shared/registration/registration-attendee-item.model';
import { RegistrationItem } from 'src/app/events-v4/shared/registration/registration-item.model';

declare var jQuery: any;

@Component({
  selector: 'wl-event-v4-registration-wizard-attendees',
  templateUrl: 'event-v4-registration-wizard-attendees.component.html',
  styleUrls: ['event-v4-registration-wizard-attendees.component.css'],
  providers: [EventV4ItemService, EventV4CustomRegFieldService],
  standalone: false,
})
export class EventV4RegistrationWizardAttendeesComponent implements IValidationComponent {
  public registrationId: number;
  public registration: RegistrationUpsert = new RegistrationUpsert();
  @Input() public registrationProfile: ProfileMin = null;
  public event: EventRead = new EventRead();
  public initialLoadComplete: boolean = false;

  public _utilService: UtilService;
  public _registrationService: EventV4RegistrationService;
  public _eventItemService: EventV4ItemService;
  public _translationService: TranslationService;
  public _eventCustomRegFieldService: EventV4CustomRegFieldService;
  public _eventFieldVisibleRequiredEnum: any = EventFieldVisibleRequiredEnum;
  public _iAmAsloAnAttendeeConfigSetting: boolean = true;

  public searchAllProfiles: boolean;
  public availableEventItems: Array<EventItemSummary>;
  public canAddNewAttendee: boolean;

  public allPossibleCustomFields: Array<EventCustomRegField>;

  public _loading: boolean = false;
  @ViewChildren(EventV4RegistrationWizardAttendeeComponent)
  public _attendeeComponents: QueryList<EventV4RegistrationWizardAttendeeComponent>;

  public constructor(
    utilService: UtilService,
    eventItemService: EventV4ItemService,
    translationService: TranslationService,
    registrationService: EventV4RegistrationService,
    eventCustomRegFieldService: EventV4CustomRegFieldService,
    public _el: ElementRef,
    public _configSettingService: ConfigSettingService
  ) {
    this._utilService = utilService;
    this._eventItemService = eventItemService;
    this._translationService = translationService;
    this._eventCustomRegFieldService = eventCustomRegFieldService;
    this._registrationService = registrationService;

    this.searchAllProfiles = false;
    this.availableEventItems = new Array<EventItemSummary>();
    this.canAddNewAttendee = false;

    this.allPossibleCustomFields = new Array<EventCustomRegField>();
  }

  public loadData = (): Promise<boolean | void> => {
    let promises: Array<Promise<any>> = [];

    this._loading = true;

    promises.push(
      this._eventCustomRegFieldService.getEventCustomRegFields(this.event.EventId).then((data: any) => {
        this.allPossibleCustomFields = data.Result;
      })
    );

    let editAttendees: Array<RegistrationAttendee> = null;

    if (this.registrationId > 0) {
      promises.push(
        this._registrationService.getRegistrationAttendeesMin(this.registrationId).then((data: Array<RegistrationAttendee>) => {
          editAttendees = data;
        })
      );
    }

    promises.push(
      this._configSettingService.getConfigSetting('', 'EventRegistration', 'IAmAlsoAnAttendee').then((setting: any) => {
        if (setting && setting.SettingValue != null && (!setting.SettingValue || setting.SettingValue.toLowerCase() == 'false'))
          this._iAmAsloAnAttendeeConfigSetting = false;
      })
    );

    return Promise.all(promises).then(
      () => {
        // post process if loading a registration
        if (this.registrationId > 0) {
          // attach custom fields to responses if there are attendee
          if (editAttendees && this.allPossibleCustomFields) {
            editAttendees.forEach((att: RegistrationAttendee) => {
              // tie custom fields to attendee
              if (att.CustomFieldResponses)
                att.CustomFieldResponses.forEach((attCustomFieldResponse) => {
                  if (!attCustomFieldResponse.CustomField) {
                    let customField = this.allPossibleCustomFields.find((x) => x.Id == attCustomFieldResponse.EventCustomRegFieldId);

                    if (customField) attCustomFieldResponse.CustomField = customField;
                  }
                });
            });
          }

          this.registration.Attendees = editAttendees ? editAttendees : [];

          this.registration.Attendees.forEach((att: RegistrationAttendee, i: number) => {
            // select Items. Api doesn't send the "Selected" property. it's pure UI.
            // but any Item in the Items array means that this attendee is joind to those Items.
            if (att.Items) att.Items.forEach((attendeeItem) => (attendeeItem.Selected = true));

            // adds the non selected registration items so use can have checkboxes
            this.addAllowedRegistrationAttendeeItemsToAttendee(att, false);
          });

          // this._registrationService.calculateTotals(this.registration);
        }

        this.initialLoadComplete = true;
        this._loading = false;
      },
      () => (this._loading = false)
    );
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();
    valid.isValid = true;

    let selectedAttendees = this.registration.Attendees.filter((attendee: RegistrationAttendee) => {
      //some name indicator must be filled out
      return (
        this._utilService.isNotNullEmptyOrWhitespace(attendee.Email) ||
        this._utilService.isNotNullEmptyOrWhitespace(attendee.FName) ||
        this._utilService.isNotNullEmptyOrWhitespace(attendee.LName)
      );
    });

    let allAttendeesHaveASelectedItem = !this.registration.Attendees.some(
      (attendee) =>
        !attendee.Items.some((attendeeItem: RegistrationAttendeeItem) => attendeeItem.Selected && attendeeItem.AttendeesForItem > 0)
    );

    if (allAttendeesHaveASelectedItem == false) {
      valid.isValid = false;
      valid.errors.push(
        this._translationService.getInstant('events.registration.wizard.attendees.attendees-not-assigned-to-item-validation-error')
      );
    }

    if (this._attendeeComponents) {
      let invalidAttendeesValidation = this._attendeeComponents.map((x) => x.getValidationResult()).filter((x) => !x.isValid);

      if (invalidAttendeesValidation.length) {
        valid.isValid = false;

        // merge errors from attendees into validation error list
        [].concat
          .apply(
            [],
            invalidAttendeesValidation.map((x) => x.errors)
          )
          .forEach((e: string) => {
            // only add the same error once
            if (valid.errors.indexOf(e) < 0) valid.errors.push(e);
          });
      }
    }

    return valid;
  };

  public updateAvailableAttendeeRegistrationItems = (autoAddOrRemoveDefaultAttendees: boolean): void => {
    this.updateCanAddNewAttendee();

    if (this.canAddNewAttendee && autoAddOrRemoveDefaultAttendees) {
      // Ensure all attendees have allowed items
      for (let attendee of this.registration.Attendees) {
        this.addAllowedRegistrationAttendeeItemsToAttendee(attendee, false);
      }

      // Round-robin assignment of items to attendees
      let availableEventItems = this.registration.Items.filter(
        (item) => item.AttendeesForItem > 0 && this.getAttendeesRemainigForRegistrationItem(item.EventItemId) > 0
      );

      let iterationCount = 0; // Limit loop execution
      const maxIterations = 50; // Prevent runaway loop

      while (availableEventItems.length > 0 && iterationCount < maxIterations) {
        iterationCount++;

        for (let itemIndex = 0; itemIndex < availableEventItems.length; itemIndex++) {
          const item = availableEventItems[itemIndex];
          let remainingQuantity = this.getAttendeesRemainigForRegistrationItem(item.EventItemId);

          if (remainingQuantity <= 0) {
            continue;
          }

          // Find an attendee who does not already have this item
          let unassignedAttendee = this.registration.Attendees.find((attendee) => {
            return !attendee.Items.some((attItem) => attItem.EventItemId === item.EventItemId && attItem.Selected);
          });

          if (unassignedAttendee && (!item.CreateSeparateAttendee || !unassignedAttendee.Items.some((x) => x.Selected))) {
            let attendeeItem = unassignedAttendee.Items.find((attItem) => attItem.EventItemId === item.EventItemId);

            if (attendeeItem) {
              attendeeItem.Selected = true; // Mark as selected

              for (let attendeecomponent of this._attendeeComponents) {
                attendeecomponent.attendeeItemSelectionChanged(attendeeItem);
              }

              // Recalculate the remaining quantity
              remainingQuantity = this.getAttendeesRemainigForRegistrationItem(item.EventItemId);
            }
          }

          // If no more remaining quantity, remove the item from the list
          if (remainingQuantity <= 0) {
            availableEventItems.splice(itemIndex, 1);
            itemIndex--; // Adjust index after removal
          }
        }
      }

      // Ensure attendees have correct items and add new attendees if needed
      this.updateCanAddNewAttendee();
      while (this.canAddNewAttendee) {
        this.newAttendee();
      }

      for (let attendee of this.registration.Attendees) {
        this.addAllowedRegistrationAttendeeItemsToAttendee(attendee, true);
      }
    } else if (autoAddOrRemoveDefaultAttendees && this.registration.Attendees.length) {
      // Handle cases where attendees exist but items need adjustment
      this.handleEventItemsAdjustment();
    }
  };

  public getRegistrationItem = (eventItemId: number): RegistrationItem => {
    let registrationItems = this.registration.Items.filter((regItem: RegistrationItem) => {
      return regItem.EventItemId == eventItemId;
    });

    if (registrationItems.length > 0) {
      return registrationItems[0];
    } else {
      return new RegistrationItem();
    }
  };

  public getAttendeesForRegistrationItem = (eventItemId: number): Array<RegistrationAttendee> => {
    let attendeesForRegItem = new Array<RegistrationAttendee>();

    for (let attendee of this.registration.Attendees) {
      for (let item of attendee.Items) {
        if (item.Selected == true && item.EventItemId == eventItemId) {
          attendeesForRegItem.push(attendee);
        }
      }
    }

    return attendeesForRegItem;
  };

  public getAttendeesRemainigForRegistrationItem = (eventItemId: number): number => {
    let attendeesAllowedToAdd = 0;

    let registrationItem = this.getRegistrationItem(eventItemId);
    if (registrationItem.Quantity > 0) {
      let attendeesForSelectedRegistrationItem = this.getAttendeesForRegistrationItem(eventItemId);

      let currentAttendeeCountForRegistrationItem = attendeesForSelectedRegistrationItem.length;
      attendeesAllowedToAdd = registrationItem.AttendeesForItem * registrationItem.Quantity - currentAttendeeCountForRegistrationItem;
    }

    return attendeesAllowedToAdd;
  };

  public addAllowedRegistrationAttendeeItemsToAttendee = (attendee: RegistrationAttendee, preselectIfAvailable: boolean): void => {
    let itemListChanged: boolean = false;

    for (let registrationItem of this.registration.Items.filter((x) => x.AttendeesForItem > 0 && x.Quantity > 0)) {
      let foundAttendeeEventItem = attendee.Items.filter((item) => item.EventItemId == registrationItem.EventItemId);

      // attendee doesn't have the item. add event item to list of possible items for the attendee
      if (!foundAttendeeEventItem.length) {
        let regsitrationAttendeeItem = new RegistrationAttendeeItem();
        this._utilService.map(registrationItem, regsitrationAttendeeItem);

        regsitrationAttendeeItem.Remaining = this.getAttendeesRemainigForRegistrationItem(registrationItem.EventItemId);

        // preselect if any available
        if (preselectIfAvailable) regsitrationAttendeeItem.Selected = regsitrationAttendeeItem.Remaining > 0;

        attendee.Items.push(regsitrationAttendeeItem);
        itemListChanged = true;
      } else {
        // copy needed properties that API doesn't populate when loading existing attendees
        if (foundAttendeeEventItem[0]) {
          foundAttendeeEventItem[0].DisplayOrder = registrationItem.DisplayOrder;
          foundAttendeeEventItem[0].AttendeesForItem = registrationItem.AttendeesForItem;
        }
      }
    }

    // sort in display order only if anything changed. it's expensive
    if (itemListChanged) {
      attendee.Items.sort((a, b) => {
        return a.DisplayOrder < b.DisplayOrder
          ? -1
          : a.DisplayOrder > b.DisplayOrder
            ? 1
            : a.Descr.toLocaleLowerCase() < b.Descr.toLocaleLowerCase()
              ? -1
              : a.Descr.toLocaleLowerCase() > b.Descr.toLocaleLowerCase()
                ? 1
                : 0;
      });
    }
  };

  public getPotentialAttendeesForRegistrationItem = (eventItemId: number): number => {
    let attendeesAllowedToAdd = 0;

    let registrationItem = this.getRegistrationItem(eventItemId);

    attendeesAllowedToAdd = registrationItem.AttendeesForItem;
    if (registrationItem.Quantity > 0) {
      attendeesAllowedToAdd = registrationItem.AttendeesForItem * registrationItem.Quantity;
      let attendeesForSelectedRegistrationItem = this.getAttendeesForRegistrationItem(eventItemId);

      attendeesAllowedToAdd - attendeesForSelectedRegistrationItem.length;
    }

    if (attendeesAllowedToAdd < 0) {
      attendeesAllowedToAdd = 0;
    }
    return attendeesAllowedToAdd;
  };

  public attendeeItemSelectionChanged = (item: RegistrationAttendeeItem, attendee: RegistrationAttendee): void => {
    if (item.Selected) {
      let remainingAttendeesAvailable = this.getAttendeesRemainigForRegistrationItem(item.EventItemId);

      // oops, selected this item one too many times
      if (remainingAttendeesAvailable < 0) {
        // find some other attendee going to this event item
        let otherAttendeeItems = this.registration.Attendees.filter((a) => a != attendee)
          .map((a) => a.Items.filter((otherItem) => otherItem.EventItemId == item.EventItemId && otherItem.Selected))
          .filter((items) => items.length > 0);

        // and take away this event item for that attendee
        if (otherAttendeeItems.length) otherAttendeeItems[0][0].Selected = false;
      }
    }

    this.updateCanAddNewAttendee();
  };

  public newAttendee = (): void => {
    let newAttendee = new RegistrationAttendee();

    newAttendee.OrgName = this.registration.Registration.OrgName;
    newAttendee.ProfileId = 0;
    if (this.registration.Attendees.length <= 0 && this._iAmAsloAnAttendeeConfigSetting) {
      //only on the first attendee
      this._utilService.mapSafe(this.registration.Registration, newAttendee);

      let fullName: string = newAttendee.LName;

      if (newAttendee.FName && newAttendee.FName.trim() != '') fullName = newAttendee.FName + ' ' + newAttendee.LName;

      newAttendee.NameForBadge = fullName;

      if (!this.registration.Registration.profileOrgInd && this.registration.Registration.ProfileId > 0)
        newAttendee.ProfileId = this.registration.Registration.ProfileId;
      else if (this.registration.Registration.relatedProfileId > 0) newAttendee.ProfileId = this.registration.Registration.relatedProfileId;
      else newAttendee.ProfileId = 0;
    }

    this.addAllowedRegistrationAttendeeItemsToAttendee(newAttendee, true);

    this.registration.Attendees.push(newAttendee);

    this.updateCanAddNewAttendee();
  };

  public removeAttendee = (attendee: RegistrationAttendee): void => {
    this._utilService.arrayRemoveEntry(this.registration.Attendees, attendee);
    this.updateCanAddNewAttendee();
  };

  public updateCanAddNewAttendee = (): void => {
    let newCanAddNewAttendee = false;
    for (let registrationItem of this.registration.Items) {
      if (registrationItem.Quantity > 0) {
        let attendeesAllowedForRegItem = this.getAttendeesRemainigForRegistrationItem(registrationItem.EventItemId);
        if (attendeesAllowedForRegItem > 0) {
          newCanAddNewAttendee = true;
        }
      }
    }

    if (this.canAddNewAttendee != newCanAddNewAttendee) this.canAddNewAttendee = newCanAddNewAttendee;
  };

  public onResponseChange = (response: any, attendee: RegistrationAttendee): void => {
    this._registrationService.calculateTotals(this.registration);
  };

  /**
   * convenience method so that while other registration items are loading, attendee's doesn't look like it's not loading
   */
  public setWillBeLoadingNext() {
    this._loading = true;
  }

  /**
   * add a new attendee
   */
  public addNewAttendee() {
    if (this._loading) return;

    this.newAttendee();
    setTimeout(() => this.scrollToAttendee(this.registration.Attendees.length - 1));
  }

  /**
   * scroll to a selected attendee
   */
  public scrollToAttendee(attendeeIndex: number) {
    this.scrollToElement('.wc-event-reg-wiz-attendee-' + attendeeIndex);
  }

  /**
   * scroll to the element targeted by the given selector inside the modal window.
   * @param selector
   */
  public scrollToElement(selector: string) {
    const targetElement = this._el.nativeElement.querySelector(selector) as HTMLElement;
    const scrollParent = this._el.nativeElement.closest('.wl-tab-content-container') as HTMLElement;

    if (!targetElement || !scrollParent) return;

    // 1. Calculate relative position
    // targetRect.top is relative to viewport, parentRect.top is relative to viewport.
    // Subtracting them gives the distance from the top of the parent.
    const targetRect = targetElement.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();

    const targetTop = targetRect.top - parentRect.top + scrollParent.scrollTop - 20;

    // 2. Define visible boundaries
    const parentHeight = scrollParent.offsetHeight;
    const currentScrollTop = scrollParent.scrollTop;
    const parentBottomEdge = currentScrollTop + parentHeight - 60;

    // 3. Scroll if outside the "visible" range
    if (targetTop < currentScrollTop || targetTop > parentBottomEdge) {
      scrollParent.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  }

  /**
   * check if field should be displayed
   * @param value
   */
  public displayField(value: number): boolean {
    //this is an attendee so look for 1 or 3 (bitwise 2)
    if ((value & 2) > 0) {
      return true;
    } else {
      return false;
    }
  }

  private getOverBookedEventItems(): Array<RegistrationItem> {
    return this.registration.Items.filter((item) => {
      let attendeesForItem = this.getAttendeesForRegistrationItem(item.EventItemId);
      return attendeesForItem.length > 0 && this.getAttendeesRemainigForRegistrationItem(item.EventItemId) < 0;
    });
  }

  private handleEventItemsAdjustment(): void {
    let emptyEventItems = this.registration.Items.filter((item) => item.AttendeesForItem > 0 && (!item.Quantity || item.Quantity <= 0));

    if (emptyEventItems.length) {
      let emptyEventItemIds = emptyEventItems.map((item) => item.EventItemId);
      for (let attendee of this.registration.Attendees.filter((att) =>
        att.Items.some((item) => emptyEventItemIds.includes(item.EventItemId))
      )) {
        attendee.Items = attendee.Items.filter((item) => !emptyEventItemIds.includes(item.EventItemId));
      }

      this.registration.Attendees = this.registration.Attendees.filter((att) => att.AttendeeId > 0 || att.Items.length > 0);
    }

    let overBookedEventItems = this.getOverBookedEventItems();
    while (overBookedEventItems.length) {
      overBookedEventItems.forEach((overBookedEventItem) => {
        let attendeeItems = this.registration.Attendees.map((att) =>
          att.Items.filter((attItem) => attItem.EventItemId === overBookedEventItem.EventItemId && attItem.Selected)
        ).filter((items) => items.length > 0);

        if (attendeeItems.length) {
          attendeeItems[attendeeItems.length - 1][0].Selected = false;
        }
      });
      overBookedEventItems = this.getOverBookedEventItems();
    }
  }
}
